import { useSession } from 'next-auth/react';
import { useRecoilValue } from 'recoil';

import { JourneyHeader } from '@/components/modules/journey/header';
import { LineNameList } from '@/components/modules/line/name-list';
import { RatesSummaryCard } from '@/components/modules/option/summary-card/rates';
import { MODE_WATER, USER_ACCESS_FEATURES } from '@/lib/constants';
import { getServiceLineNames } from '@/lib/models/lines/utils/get-line-service-ids';
import { OptionModel } from '@/lib/models/option/types';
import { getOptionTransferLocationIds } from '@/lib/models/option/utils/get-option-transfer-location-ids';
import { featuresState } from '@/state/features';
import { getLinesByIds } from '@/state/lines';
import { SegmentModeType } from '@/types/api-types';
import { canAccessFeature } from '@/utils/auth';
import { tv } from '@/utils/styles';

import { OptionViaBadge } from '../via-badge';

export interface OptionHeaderProps {
  option: OptionModel;
  mode: SegmentModeType;
  originId: string;
  destinationId: string;
}

const OptionHeader = ({ option, mode, originId, destinationId }: OptionHeaderProps) => {
  const { data: session } = useSession();
  const features = useRecoilValue(featuresState);
  const canAccessRates = canAccessFeature(USER_ACCESS_FEATURES.RATES, session);

  const { via, lineIds, carrierIds } = option;

  const transferLocationIds = getOptionTransferLocationIds(option);
  const lines = useRecoilValue(getLinesByIds(lineIds));
  const lineServiceNames = lines.map((line) => getServiceLineNames(line, carrierIds));

  const shouldShowRates = features.showSpotRates && canAccessRates && mode === MODE_WATER;

  const { base, meta, lineWrapper } = styles({ shouldShowRates });

  return (
    <div className={base()}>
      <div>
        <JourneyHeader
          originId={originId}
          destinationId={destinationId}
          transferLocationIds={transferLocationIds}
          mode={mode}
          carrierIds={option.carrierIds}
        />
        <div className={meta()}>
          {Boolean(lineServiceNames.length > 0) && (
            <div className={lineWrapper()}>
              Services: <LineNameList lineIds={option.lineIds} carrierIds={option.carrierIds} />
            </div>
          )}
          {via && via.length > 0 && <OptionViaBadge via={via} />}
        </div>
      </div>
      <div />
      {shouldShowRates && (
        <RatesSummaryCard originId={originId} destinationId={destinationId} carrierIds={option.carrierIds} />
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'grid grid-cols-[3fr_1fr_1fr] gap-4 bg-white p-4 md:px-8 md:py-6',
    meta: 'mt-4 flex items-center gap-4',
    lineWrapper: 'text-sm italic',
  },
  variants: {
    shouldShowRates: {
      true: {
        base: 'grid-cols-[3fr_1fr_1fr_1fr]',
      },
    },
  },
});

export { OptionHeader };
